import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { recruitmentApi } from "../../services/recruitmentApi";
import { formatApiErrorDetail } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { StoreSelect } from "../../components/StoreSelect";
import { usePermissions } from "../../hooks/usePermissions";
import { ReasonDialog } from "../../components/ReasonDialog";
import { useLocalization } from "../../hooks/useLocalization";

const EMPTY_FORM = {
  title_en: "", title_ja: "", department: "", store_location: null, employment_type: "PART_TIME",
  description_en: "", description_ja: "", requirements_en: "", requirements_ja: "",
  has_shift_premium: false, hero_image_alt_en: "", hero_image_alt_ja: "",
};

export default function RecruitmentJobsPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageRecruitment } = usePermissions();
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [closingJobId, setClosingJobId] = useState(null);

  const load = () => recruitmentApi.listJobs().then((r) => setJobs(r.data)).catch(() => {});

  useEffect(() => {
    if (!canManageRecruitment) return;
    load();
  }, [canManageRecruitment]);

  if (!canManageRecruitment) return <p className="text-sm text-gray-500" data-testid="recruitment-jobs-access-denied">{t("common.accessDenied")}</p>;

  const startEdit = (job) => {
    setEditingId(job.id);
    setForm({
      title_en: job.title_en, title_ja: job.title_ja, department: job.department || "",
      store_location: job.store_location || "", employment_type: job.employment_type,
      description_en: job.description_en, description_ja: job.description_ja,
      requirements_en: job.requirements_en || "", requirements_ja: job.requirements_ja || "",
      has_shift_premium: !!job.has_shift_premium, hero_image_alt_en: job.hero_image_alt_en || "", hero_image_alt_ja: job.hero_image_alt_ja || "",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingId) {
        await recruitmentApi.updateJob(editingId, form);
        toast.success(t("mgmtRecruitment.jobUpdated"));
      } else {
        await recruitmentApi.createJob(form);
        toast.success(t("mgmtRecruitment.jobCreatedDraft"));
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleHeroImage = async (job, file) => {
    if (!file) return;
    try {
      await recruitmentApi.uploadHeroImage(job.id, file);
      toast.success(t("mgmtRecruitment.heroImageUploaded"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleStatus = async (job) => {
    const next = job.status === "OPEN" ? "CLOSED" : "OPEN";
    if (next === "CLOSED") { setClosingJobId(job.id); return; }
    try {
      await recruitmentApi.setJobStatus(job.id, next, undefined);
      toast.success(next === "OPEN" ? t("mgmtRecruitment.jobOpened") : t("mgmtRecruitment.jobClosed"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const closingJob = jobs.find((j) => j.id === closingJobId);

  const confirmClose = async (closureReason) => {
    await recruitmentApi.setJobStatus(closingJobId, "CLOSED", closureReason);
    toast.success(t("mgmtRecruitment.jobClosed"));
    load();
  };

  return (
    <div className="space-y-4" data-testid="recruitment-jobs-page">
      <PageHelpBanner pageKey="recruitment-jobs" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtRecruitment.jobsHelp") }} />
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtRecruitment.jobsTitle")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtRecruitment.jobsSubtitle")}</p>
        </div>
        <button data-testid="new-job-posting-button" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); setShowForm((s) => !s); }} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
          <Plus size={15} /> {t("mgmtRecruitment.newJobPosting")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="job-posting-form">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="job-title-en-input" required placeholder={t("mgmtRecruitment.titleEnPlaceholder")} value={form.title_en} onChange={(e) => setForm((f) => ({ ...f, title_en: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="job-title-ja-input" required placeholder={t("mgmtRecruitment.titleJaPlaceholder")} value={form.title_ja} onChange={(e) => setForm((f) => ({ ...f, title_ja: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="job-department-input" placeholder={t("mgmtRecruitment.departmentPlaceholder")} value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <StoreSelect testId="job-store-location-input" value={form.store_location} onChange={(v) => setForm((f) => ({ ...f, store_location: v }))} allowEmpty className="px-3 py-2" />
            <select data-testid="job-employment-type-select" value={form.employment_type} onChange={(e) => setForm((f) => ({ ...f, employment_type: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm col-span-2">
              {["PART_TIME", "FULL_TIME", "CONTRACT"].map((et) => <option key={et} value={et}>{et.replaceAll("_", " ")}</option>)}
            </select>
          </div>
          <textarea data-testid="job-description-en-input" required placeholder={t("mgmtRecruitment.descriptionEnPlaceholder")} value={form.description_en} onChange={(e) => setForm((f) => ({ ...f, description_en: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" rows={2} />
          <textarea data-testid="job-description-ja-input" required placeholder={t("mgmtRecruitment.descriptionJaPlaceholder")} value={form.description_ja} onChange={(e) => setForm((f) => ({ ...f, description_ja: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" rows={2} />
          <textarea data-testid="job-requirements-en-input" placeholder={t("mgmtRecruitment.requirementsEnPlaceholder")} value={form.requirements_en} onChange={(e) => setForm((f) => ({ ...f, requirements_en: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" rows={2} />
          <textarea data-testid="job-requirements-ja-input" placeholder={t("mgmtRecruitment.requirementsJaPlaceholder")} value={form.requirements_ja} onChange={(e) => setForm((f) => ({ ...f, requirements_ja: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" rows={2} />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input data-testid="job-has-shift-premium-checkbox" type="checkbox" checked={form.has_shift_premium} onChange={(e) => setForm((f) => ({ ...f, has_shift_premium: e.target.checked }))} />
            {t("mgmtRecruitment.hasShiftPremiumLabel")}
          </label>
          {form.has_shift_premium && (
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="job-hero-image-alt-en-input" placeholder={t("mgmtRecruitment.heroAltEnPlaceholder")} value={form.hero_image_alt_en} onChange={(e) => setForm((f) => ({ ...f, hero_image_alt_en: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
              <input data-testid="job-hero-image-alt-ja-input" placeholder={t("mgmtRecruitment.heroAltJaPlaceholder")} value={form.hero_image_alt_ja} onChange={(e) => setForm((f) => ({ ...f, hero_image_alt_ja: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            </div>
          )}
          {error && <p className="text-sm text-red-500" data-testid="job-posting-form-error">{error}</p>}
          <button data-testid="submit-job-posting-button" type="submit" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">{editingId ? t("common.save") : t("mgmtScheduling.createDraft")}</button>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colTitle")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colStore")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colType")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colApplicants")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colAction")}</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.id} className="border-t border-gray-100" data-testid={`job-posting-row-${job.id}`}>
                <td className="px-4 py-3 font-semibold text-gray-800">{job.title_en}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{job.store_location || "-"}</td>
                <td className="px-4 py-3 text-xs">{job.employment_type?.replaceAll("_", " ")}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={job.status} />
                  {job.status === "CLOSED" && job.closure_reason && (
                    <p className="text-[11px] text-gray-500 mt-1 max-w-[180px]" data-testid={`job-closure-reason-${job.id}`}>{job.closure_reason}</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <button data-testid={`view-applicants-for-job-${job.id}`} onClick={() => navigate(`/management/recruitment/applicants?job_posting_id=${job.id}`)} className="text-blue-600 text-xs font-semibold hover:underline">{t("mgmtRecruitment.viewApplicants")}</button>
                </td>
                <td className="px-4 py-3 flex items-center gap-2">
                  <button data-testid={`edit-job-posting-${job.id}`} onClick={() => startEdit(job)} className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200">{t("mgmtRecruitment.edit")}</button>
                  {job.status !== "DRAFT" ? (
                    <button data-testid={`toggle-job-status-${job.id}`} onClick={() => toggleStatus(job)} className={`text-xs font-semibold px-2 py-1 rounded-full ${job.status === "OPEN" ? "bg-gray-100 text-gray-600" : "bg-emerald-100 text-emerald-700"}`}>
                      {job.status === "OPEN" ? t("mgmtRecruitment.close") : t("mgmtRecruitment.reopen")}
                    </button>
                  ) : (
                    <button data-testid={`toggle-job-status-${job.id}`} onClick={() => toggleStatus(job)} className="text-xs font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                      {t("mgmtRecruitment.publish")}
                    </button>
                  )}
                  <label className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 cursor-pointer">
                    {job.hero_image_paths ? t("mgmtRecruitment.replaceImage") : t("mgmtRecruitment.addImage")}
                    <input data-testid={`job-hero-image-input-${job.id}`} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleHeroImage(job, e.target.files[0])} />
                  </label>
                </td>
              </tr>
            ))}
            {jobs.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">{t("mgmtRecruitment.noJobs")}</td></tr>}
          </tbody>
        </table>
      </div>

      {closingJob && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setClosingJobId(null)} title={t("mgmtRecruitment.closeJobPostingTitle")} required={false}
          contextItems={[{ label: t("mgmtRecruitment.position"), value: closingJob.title_en }, { label: t("mgmtScheduling.colStore"), value: closingJob.store_location || t("mgmtScheduling.everyStoreLabel") }]}
          reasonLabel={t("mgmtRecruitment.closeReasonLabel")}
          confirmLabel={t("mgmtRecruitment.closePosting")} onConfirm={confirmClose}
          testId="close-job-posting-dialog"
        />
      )}
    </div>
  );
}
